import { Menu, PanelLeftClose, PanelLeftOpen, Search, Wifi, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useI18n, type MessageKey } from '@/i18n';
import { getConnectionState, onConnectionState, type ConnectionState } from '@/lib/socket';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';

const CONNECTION_COPY: Record<ConnectionState, { labelKey: MessageKey; tone: string }> = {
  idle: { labelKey: 'topbar.connection.idle', tone: 'text-ink-subtle' },
  connecting: { labelKey: 'topbar.connection.connecting', tone: 'text-ink-muted' },
  connected: { labelKey: 'topbar.connection.connected', tone: 'text-ok' },
  reconnecting: { labelKey: 'topbar.connection.reconnecting', tone: 'text-warn' },
  failed: { labelKey: 'topbar.connection.failed', tone: 'text-danger' },
};

export function Topbar({
  onOpenCommand,
  onOpenNav,
  navCollapsed,
  onToggleNav,
}: {
  onOpenCommand: () => void;
  onOpenNav: () => void;
  navCollapsed: boolean;
  onToggleNav: () => void;
}) {
  const [connection, setConnection] = useState<ConnectionState>(getConnectionState);
  const { t } = useI18n();

  useEffect(() => onConnectionState(setConnection), []);

  const status = CONNECTION_COPY[connection];
  const sidebarLabel = t(navCollapsed ? 'nav.showSidebar' : 'nav.hideSidebar');

  return (
    <header className="flex h-topbar shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenNav}
        aria-label={t('nav.open')}
      >
        <Menu className="h-4 w-4" />
      </Button>

      {/*
        Lives in the topbar rather than in the sidebar itself: a control that
        goes away with the panel it hides leaves nothing to bring it back.
      */}
      <Tooltip
        side="bottom"
        content={
          <span className="flex items-center gap-1.5">
            {sidebarLabel}
            <span className="font-mono text-canvas/60">⌘B</span>
          </span>
        }
      >
        <Button
          variant="ghost"
          size="icon"
          className="hidden lg:inline-flex"
          onClick={onToggleNav}
          aria-label={sidebarLabel}
          aria-pressed={navCollapsed}
        >
          {navCollapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </Button>
      </Tooltip>

      <button
        type="button"
        onClick={onOpenCommand}
        className="group flex h-7 w-full max-w-[320px] items-center gap-2 rounded border border-line bg-surface-sunken px-2 text-left text-sm text-ink-subtle transition-colors hover:border-line-strong"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="flex-1 truncate">{t('topbar.search')}</span>
        <span className="kbd">⌘K</span>
      </button>

      <div className="ml-auto flex items-center gap-3">
        <Tooltip
          content={t(
            connection === 'connected'
              ? 'topbar.connection.liveHint'
              : 'topbar.connection.pausedHint',
          )}
        >
          <span
            className={cn('flex items-center gap-1.5 text-xs font-medium', status.tone)}
            aria-live="polite"
          >
            {connection === 'connected' ? (
              <Wifi className="h-3.5 w-3.5" />
            ) : (
              <WifiOff className="h-3.5 w-3.5" />
            )}
            <span className="hidden sm:inline">{t(status.labelKey)}</span>
          </span>
        </Tooltip>
      </div>
    </header>
  );
}
